import { memo } from 'react';
import { PinIcon, PhoneIcon, MailIcon, GlobeIcon } from './icons';
import { telHref } from '../utils/content';

/**
 * FindMe — "Find Me / Get In Touch": contact details, office hours, and the
 * office map in three columns. Collapses entirely when there is nothing to
 * show; with only phone/email it renders the condensed centered tiles instead.
 */
export const FindMe = memo(
  ({ isCompany, addressLines = [], phone, email, languages = [], hoursRows = [], mapEmbedSrc, mapsHref }) => {
    const hasContact = addressLines.length || phone || email || languages.length;
    const hasHours = hoursRows.length > 0;
    const hasMap = !!mapEmbedSrc;
    if (!hasContact && !hasHours && !hasMap) return null;

    const condensed = !addressLines.length && !hasHours && !hasMap;
    const eyebrow = isCompany ? 'Find Us' : 'Find Me';

    if (condensed) {
      return (
        <section className="cw-section section-findme" id="find-me">
          <div className="cw-container">
            <header className="section-head reveal">
              <h2 className="section-title">Get In Touch</h2>
            </header>
            <ul className="findme-tiles reveal">
              {phone && (
                <li>
                  <span className="findme-tile-icon">
                    <PhoneIcon size={16} />
                  </span>
                  <span className="findme-tile-label">Phone</span>
                  <a href={telHref(phone.label)}>{phone.label}</a>
                </li>
              )}
              {email && (
                <li>
                  <span className="findme-tile-icon">
                    <MailIcon size={16} />
                  </span>
                  <span className="findme-tile-label">Email</span>
                  <a href={`mailto:${email}`}>{email}</a>
                </li>
              )}
            </ul>
          </div>
        </section>
      );
    }

    return (
      <section className="cw-section section-findme" id="find-me">
        <div className="cw-container">
          <header className="section-head reveal">
            <p className="section-eyebrow">{eyebrow}</p>
            <h2 className="section-title">Get In Touch</h2>
            <span className="section-bar" aria-hidden="true" />
          </header>

          <div className="findme-grid reveal">
            {hasContact && (
              <div className="findme-col">
                <h3 className="findme-label">Contact</h3>
                <ul className="findme-list">
                  {addressLines.length > 0 && (
                    <li>
                      <span className="findme-icon">
                        <PinIcon size={15} />
                      </span>
                      <span>
                        <span className="findme-term">Address</span>
                        {addressLines.map(line => (
                          <span key={line} className="findme-value">
                            {line}
                          </span>
                        ))}
                      </span>
                    </li>
                  )}
                  {phone && (
                    <li>
                      <span className="findme-icon">
                        <PhoneIcon size={15} />
                      </span>
                      <span>
                        <span className="findme-term">Phone</span>
                        <a className="findme-value" href={telHref(phone.label)}>
                          {phone.label}
                        </a>
                      </span>
                    </li>
                  )}
                  {email && (
                    <li>
                      <span className="findme-icon">
                        <MailIcon size={15} />
                      </span>
                      <span>
                        <span className="findme-term">Email</span>
                        <a className="findme-value" href={`mailto:${email}`}>
                          {email}
                        </a>
                      </span>
                    </li>
                  )}
                  {languages.length > 0 && (
                    <li>
                      <span className="findme-icon">
                        <GlobeIcon size={15} />
                      </span>
                      <span>
                        <span className="findme-term">Languages</span>
                        <span className="findme-tags">
                          {languages.map(lang => (
                            <span key={lang} className="findme-tag">
                              {lang}
                            </span>
                          ))}
                        </span>
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            )}

            {hasHours && (
              <div className="findme-col">
                <h3 className="findme-label">Office Hours</h3>
                <table className="hours-table">
                  <tbody>
                    {hoursRows.map(row => (
                      <tr key={row.id} className={row.isToday ? 'today' : undefined}>
                        <td className="hours-days">{row.days}</td>
                        <td className={`hours-time${row.isClosed ? ' hours-closed' : ''}`}>{row.time}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {hasMap && (
              <div className="findme-col">
                <h3 className="findme-label">Location</h3>
                <div className="map-frame">
                  <iframe
                    title="Office location map"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    allowFullScreen
                    src={mapEmbedSrc}
                  />
                </div>
                {mapsHref && (
                  <a className="map-directions" href={mapsHref} target="_blank" rel="noopener noreferrer">
                    Get directions ↗
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }
);
